import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { useFeatures, useMe } from "../../features/auth/api.js";
import styles from "./AppShell.module.css";
import { CommandPalette } from "./CommandPalette.js";
import { MobileNav } from "./MobileNav.js";
import { NavRail } from "./NavRail.js";
import { TopBar } from "./TopBar.js";

export function AppShell() {
  const me = useMe();
  const features = useFeatures();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K / Ctrl-K opens the palette; Esc closes layered surfaces (doc 01 §1.6/§1.9).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Show Loading on the first fetch (no cached data) AND while a refetch is in
  // flight over stale null — e.g. right after login, when ["me"] is being
  // re-fetched as authenticated. Bouncing on stale null here is the root of the
  // "log in twice" bug; wait for the refetch to settle before deciding.
  if (me.isLoading || (me.isFetching && !me.data)) {
    return <div className={styles.loading}>Loading…</div>;
  }
  if (!me.data) {
    return <Navigate to="/login" replace />;
  }

  // AI module flag from the server capabilities payload (doc 13 §2.1/§2.2):
  // off removes the AI Agent nav entry, palette result and screen.
  const aiEnabled = features.data?.aiModule ?? false;

  return (
    <div className={styles.shell}>
      <TopBar onOpenPalette={() => setPaletteOpen(true)} />
      <NavRail aiEnabled={aiEnabled} />
      <main className={styles.main}>
        <Outlet />
      </main>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        aiEnabled={aiEnabled}
      />
      <MobileNav aiEnabled={aiEnabled} />
    </div>
  );
}
